import { useCallback, useLayoutEffect, useRef } from "react";
import { Box } from "@mantine/core";
import { Link } from "react-router-dom";
import type { CompanyProfileLinkState } from "@/shared/utils/companyProfileLink.util";

export type TableRowLinkOverlayProps = {
  to: string;
  ariaLabel: string;
  linkState?: CompanyProfileLinkState;
  /** Cells matching this selector are excluded from the link width (e.g. action buttons). */
  excludeCellSelector?: string;
};

/**
 * One link per table row spanning all data columns (not action cells).
 * Enables right-click "open in new tab" on padding and gaps between cells.
 */
export default function TableRowLinkOverlay({
  to,
  ariaLabel,
  linkState,
  excludeCellSelector = "td[data-action-cell]",
}: TableRowLinkOverlayProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const linkRef = useRef<HTMLAnchorElement>(null);

  const positionLink = useCallback(() => {
    const host = hostRef.current;
    const link = linkRef.current;
    if (!host || !link) return;

    const tr = host.closest("tr");
    const table = host.closest("table");
    const td = host.closest("td");
    if (!tr || !table || !td) return;

    const tableRect = table.getBoundingClientRect();
    const tdRect = td.getBoundingClientRect();
    const trRect = tr.getBoundingClientRect();
    const actionsCell = excludeCellSelector
      ? tr.querySelector<HTMLTableCellElement>(excludeCellSelector)
      : null;
    const actionsRect = actionsCell?.getBoundingClientRect();

    const linkRight = actionsRect && actionsRect.width > 0 ? actionsRect.left : tableRect.right;
    const linkWidth = Math.max(0, linkRight - tableRect.left);
    const linkLeft = tableRect.left - tdRect.left;

    link.style.position = "absolute";
    link.style.top = "0";
    link.style.left = `${linkLeft}px`;
    link.style.width = `${linkWidth}px`;
    link.style.height = `${trRect.height}px`;
    link.style.zIndex = "1";
  }, [excludeCellSelector]);

  useLayoutEffect(() => {
    positionLink();
    const host = hostRef.current;
    if (!host) return;

    const tr = host.closest("tr");
    const table = host.closest("table");
    const resizeObserver = new ResizeObserver(positionLink);
    if (tr) resizeObserver.observe(tr);
    if (table) resizeObserver.observe(table);
    window.addEventListener("resize", positionLink);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", positionLink);
    };
  }, [positionLink, to, ariaLabel]);

  return (
    <Box
      ref={hostRef}
      aria-hidden
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: 0,
        height: 0,
        overflow: "visible",
        pointerEvents: "none",
      }}
    >
      <Box
        component={Link}
        ref={linkRef}
        to={to}
        state={linkState}
        aria-label={ariaLabel}
        tabIndex={-1}
        className="gt-row-link-overlay"
        style={{ display: "block", pointerEvents: "auto" }}
      />
    </Box>
  );
}
